import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { saveDownload } from "@/lib/reporting";
import {
  EmptyState, Field, inputCls, Modal, Page, PageHeader, PrimaryButton, StatusBadge,
} from "@/components/lims";
import {
  ClipboardText, DownloadSimple, NotePencil, PaperPlaneTilt, Plus, Signature, XCircle,
} from "@phosphor-icons/react";

// The client-facing wording for each requisition signing state (issue #125).
const SIGNATURE_LABELS = {
  pending: "Awaiting your signature",
  signed: "Requisition signed",
  declined: "Signature declined",
  expired: "Signature request expired",
};

const emptyForm = {
  project: "", po_number: "", priority: "Routine", due_date: "",
  requested_tests: [], notes: "",
};

export default function PortalOrders() {
  const [orders, setOrders] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [orderResponse, catalogueResponse] = await Promise.all([
        api.get("/portal/orders"), api.get("/portal/catalogue"),
      ]);
      setOrders(orderResponse.data);
      setCatalogue(catalogueResponse.data);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Opening Test Requests acknowledges order-decision notifications
    // (issue #94).
    api.post("/notifications/read").catch(() => {});
  }, []);

  const beginCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };
  const beginEdit = (order) => {
    setEditing(order);
    setForm({
      project: order.project || "", po_number: order.po_number || "",
      priority: order.priority || "Routine", due_date: order.due_date || "",
      requested_tests: (order.requested_tests || []).map((row) => ({ assay_id: row.assay_id })),
      notes: order.notes || "",
    });
    setOpen(true);
  };
  const set = (key) => (event) => setForm({ ...form, [key]: event.target.value });
  const toggleAssay = (assay) => {
    const selected = form.requested_tests.some((row) => row.assay_id === assay.id);
    setForm({
      ...form,
      requested_tests: selected
        ? form.requested_tests.filter((row) => row.assay_id !== assay.id)
        : [...form.requested_tests, { assay_id: assay.id }],
    });
  };
  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/portal/orders/${editing.id}`, form);
      else await api.post("/portal/orders", form);
      toast.success(editing ? "Draft updated" : "Order draft created");
      setOpen(false);
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || error.message);
    } finally {
      setSaving(false);
    }
  };
  const act = async (order, action) => {
    if (action === "cancel" && !window.confirm(`Cancel ${order.order_code}?`)) return;
    try {
      await api.post(`/portal/orders/${order.id}/${action}`);
      toast.success(action === "submit" ? "Order submitted for approval" : "Order cancelled");
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || error.message);
    }
  };
  const downloadRequisition = async (order) => {
    try {
      const { data } = await api.get(`/portal/orders/${order.id}/signed-requisition.pdf`, { responseType: "blob" });
      saveDownload(data, `requisition-${order.order_code}.pdf`, "application/pdf");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || error.message);
    }
  };

  return (
    <Page>
      <PageHeader
        title="Test Requests"
        subtitle="Prepare orders, submit them for laboratory approval, and respond to revision requests"
        action={<PrimaryButton onClick={beginCreate}><Plus size={18} /> New request</PrimaryButton>}
      />

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        {loading ? (
          <div className="p-8 space-y-3">{[0, 1, 2].map((row) => <div key={row} className="h-10 rounded bg-slate-100 animate-pulse" />)}</div>
        ) : orders.length === 0 ? (
          <EmptyState icon={ClipboardText} title="No test requests" subtitle="Create a draft when you are ready to send work to the laboratory" />
        ) : (
          <table className="w-full text-sm" data-testid="portal-order-list">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-5 py-3 text-left">Order</th><th className="px-5 py-3 text-left">Project</th><th className="px-5 py-3 text-left">Requested tests</th><th className="px-5 py-3 text-left">Due</th><th className="px-5 py-3 text-left">Status</th><th className="px-5 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {orders.map((order) => (
                <tr key={order.id}>
                  <td className="px-5 py-4 font-mono text-xs text-slate-600">{order.order_code}</td>
                  <td className="px-5 py-4"><div className="font-medium text-slate-900">{order.project || "Untitled request"}</div><div className="text-xs text-slate-400">{order.po_number || "No PO number"}</div></td>
                  <td className="px-5 py-4 text-slate-600">{(order.requested_tests || []).map((row) => row.assay_name).join(", ") || "None selected"}</td>
                  <td className="px-5 py-4 text-slate-500">{order.due_date || "—"}</td>
                  <td className="px-5 py-4">
                    <StatusBadge status={order.status} />
                    {order.decision_reason && <p className="mt-1 max-w-xs text-xs text-status-critical">{order.decision_reason}</p>}
                    {order.requisition_signature && (
                      <div className="mt-1 flex items-center gap-2 text-xs text-slate-500" data-testid={`requisition-signature-${order.order_code}`}>
                        <span>{SIGNATURE_LABELS[order.requisition_signature.status] || order.requisition_signature.status}</span>
                        {order.requisition_signature.sign_url && (
                          <a href={order.requisition_signature.sign_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded bg-slate-900 px-2 py-1 text-white">
                            <Signature size={14} /> Sign requisition
                          </a>
                        )}
                        {order.requisition_signature.has_document && (
                          <button onClick={() => downloadRequisition(order)} className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 hover:border-slate-400">
                            <DownloadSimple size={14} /> Signed PDF
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-4"><div className="flex justify-end gap-2">
                    {order.can_edit && <button onClick={() => beginEdit(order)} className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-xs hover:border-slate-400"><NotePencil size={14} /> Edit</button>}
                    {order.can_submit && <button onClick={() => act(order, "submit")} className="inline-flex items-center gap-1 rounded bg-slate-900 px-2 py-1 text-xs text-white"><PaperPlaneTilt size={14} /> Submit</button>}
                    {order.can_cancel && <button aria-label={`Cancel ${order.order_code}`} onClick={() => act(order, "cancel")} className="rounded p-1.5 text-slate-400 hover:bg-status-critical/10 hover:text-status-critical"><XCircle size={17} /></button>}
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? `Edit ${editing.order_code}` : "New test request"} testid="portal-order-modal">
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Project"><input required value={form.project} onChange={set("project")} className={inputCls} /></Field>
            <Field label="PO number"><input value={form.po_number} onChange={set("po_number")} className={inputCls} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Priority"><select value={form.priority} onChange={set("priority")} className={inputCls}>{["Routine", "Urgent", "STAT"].map((priority) => <option key={priority}>{priority}</option>)}</select></Field>
            <Field label="Requested due date"><input type="date" value={form.due_date} onChange={set("due_date")} className={inputCls} /></Field>
          </div>
          <Field label="Requested tests">
            <div className="flex flex-wrap gap-2">
              {catalogue.map((assay) => {
                const selected = form.requested_tests.some((row) => row.assay_id === assay.id);
                return <button type="button" key={assay.id} onClick={() => toggleAssay(assay)} className={`rounded-full border px-3 py-1.5 text-xs ${selected ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600"}`}>{assay.name}</button>;
              })}
            </div>
          </Field>
          <Field label="Notes"><textarea rows={3} value={form.notes} onChange={set("notes")} className={inputCls} /></Field>
          <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={() => setOpen(false)} className="rounded px-4 py-2 text-sm text-slate-600 hover:bg-slate-100">Close</button><PrimaryButton disabled={saving}>{editing ? "Save changes" : "Save draft"}</PrimaryButton></div>
        </form>
      </Modal>
    </Page>
  );
}
